import { useContext, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LanguageContext } from './LanguageContext'
import { isLocalized, localizedPath, splitLocale } from '../../config/i18nRoutes'

/**
 * Keeps the URL and the site language in step, in both directions:
 *
 *  - Arriving on a language URL (/de/services) switches the site to that
 *    language — this is how search visitors and the prerender get German.
 *  - Switching language with the switcher while on a page that has language
 *    URLs moves to the matching URL (/services → /fr/services and back).
 *  - Following an ordinary link (/process) while German is selected lands on
 *    /de/process, so the address always matches what is on screen.
 *
 * Renders nothing.
 */
const LocaleUrlSync = () => {
  const { selectedLanguage, changeLanguage } = useContext(LanguageContext)
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const lastPath = useRef(null)
  const lastLang = useRef(null)

  useEffect(() => {
    const { lang: urlLang, base } = splitLocale(pathname)
    const pathChanged = lastPath.current !== pathname
    // Only a change of language on the same page counts as using the switcher.
    // Comparing with the previous language (not just the previous path) keeps
    // StrictMode's repeated effect run from mistaking a stale stored language
    // for a switch.
    const langChanged = lastLang.current !== null && lastLang.current !== selectedLanguage
    lastPath.current = pathname
    lastLang.current = selectedLanguage

    if (urlLang) {
      if (selectedLanguage === urlLang) return
      if (langChanged && !pathChanged) {
        // Same page, language switched: move to that language's URL.
        navigate(localizedPath(base, selectedLanguage) + search + hash, { replace: true })
      } else {
        // A page in a given language: the URL decides.
        changeLanguage(urlLang)
      }
      return
    }

    // A root (English) URL while German, French or Spanish is selected.
    const target = localizedPath(base, selectedLanguage)
    if (target !== pathname && isLocalized(base)) {
      navigate(target + search + hash, { replace: true })
    }
  }, [pathname, search, hash, selectedLanguage, changeLanguage, navigate])

  return null
}

export default LocaleUrlSync
